import React, { useState, useMemo, useRef, useEffect } from 'react';
import dayjs from 'dayjs';

function CsvTable({ headers, rows, onCellEdit, onAddRow, onDeleteRow, editable, comments = [], onAddComment, onResolve, selectable = false, selectedRows = new Set(), onSelectionChange, rowReviews = {}, currentUserAlias, highlightedRow = null, onCommentIconClick, codeGenEnabled = false, onRowCodeGen, onRowCopyAgent, generatingCode = false }) {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [commentPopup, setCommentPopup] = useState(null); // { rowIndex, column, x, y }
  const [commentText, setCommentText] = useState('');
  const [hoveredCell, setHoveredCell] = useState(null);
  const [pinnedCell, setPinnedCell] = useState(null);
  const [expandedTooltipThread, setExpandedTooltipThread] = useState(null);
  const [tooltipReplyTo, setTooltipReplyTo] = useState(null);
  const [tooltipReplyText, setTooltipReplyText] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Clear pinned/hovered state when rows change (e.g. filter applied)
  useEffect(() => {
    setPinnedCell(null);
    setHoveredCell(null);
    setExpandedTooltipThread(null);
    setTooltipReplyTo(null);
    setTooltipReplyText('');
  }, [rows]);
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const columnPickerRef = useRef(null);

  const visibleHeaders = useMemo(() =>
    headers.filter(h => !hiddenColumns.has(h)),
    [headers, hiddenColumns]
  );

  const toggleColumn = (header) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      next.has(header) ? next.delete(header) : next.add(header);
      return next;
    });
  };

  const handleResizeStart = (e, header) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const th = e.target.parentElement;
    const startWidth = th.offsetWidth;
    const onMouseMove = (moveEvent) => {
      const newWidth = Math.max(60, startWidth + (moveEvent.clientX - startX));
      setColumnWidths(prev => ({ ...prev, [header]: newWidth }));
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Build a thread-aware map of cellRef -> threads for quick lookup
  const commentThreadMap = useMemo(() => {
    const parentsByCellRef = {};
    const replyMap = {};
    (comments || []).forEach(c => {
      if (c.parentCommentId) {
        if (!replyMap[c.parentCommentId]) replyMap[c.parentCommentId] = [];
        replyMap[c.parentCommentId].push(c);
      } else if (c.cellRef) {
        if (!parentsByCellRef[c.cellRef]) parentsByCellRef[c.cellRef] = [];
        parentsByCellRef[c.cellRef].push(c);
      }
    });
    Object.values(replyMap).forEach(arr => arr.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
    const map = {};
    for (const [ref, parents] of Object.entries(parentsByCellRef)) {
      parents.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      map[ref] = parents.map(p => ({ ...p, replies: replyMap[p.commentId] || [] }));
    }
    return map;
  }, [comments]);

  // Legacy flat map for indicator count
  const commentMap = useMemo(() => {
    const map = {};
    (comments || []).forEach(c => {
      if (c.cellRef) {
        if (!map[c.cellRef]) map[c.cellRef] = [];
        map[c.cellRef].push(c);
      }
    });
    return map;
  }, [comments]);

  const handleDoubleClick = (rowIndex, column, currentValue) => {
    if (!editable) return;
    setEditingCell({ rowIndex, column });
    setEditValue(currentValue || '');
  };

  const handleEditSave = () => {
    if (editingCell) {
      onCellEdit(editingCell.rowIndex, editingCell.column, editValue);
      setEditingCell(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleEditSave();
    if (e.key === 'Escape') setEditingCell(null);
  };

  const handleContextMenu = (e, rowIndex, column) => {
    e.preventDefault();
    setCommentPopup({ rowIndex, column, x: e.clientX, y: e.clientY });
    setCommentText('');
  };

  const handleCommentSubmit = () => {
    if (!commentText.trim() || !commentPopup || !onAddComment) return;
    const cellRef = `R${commentPopup.rowIndex + 1}:${commentPopup.column}`;
    onAddComment(commentText.trim(), cellRef);
    setCommentPopup(null);
    setCommentText('');
  };

  const getCellComments = (rowIndex, column) => {
    const cellRef = `R${rowIndex + 1}:${column}`;
    return commentMap[cellRef] || [];
  };

  const getCellThreads = (rowIndex, column) => {
    const cellRef = `R${rowIndex + 1}:${column}`;
    return commentThreadMap[cellRef] || [];
  };

  const getColumnClass = (header) => {
    const h = header.toLowerCase().replace(/\s+/g, '-');
    if (h === 'title') return 'col-title';
    if (h === 'preconditions' || h === 'pre-conditions') return 'col-preconditions';
    if (h === 'steps') return 'col-steps';
    if (h === 'expected-result' || h === 'expected-results') return 'col-expected-result';
    return '';
  };

  const getCellThreadStats = (rowIndex, column) => {
    const threads = getCellThreads(rowIndex, column);
    const total = threads.length;
    const unresolved = threads.filter(t => !t.resolved).length;
    return { total, unresolved };
  };

  const handleTooltipReply = (parentId, cellRef) => {
    if (!tooltipReplyText.trim() || !onAddComment) return;
    onAddComment(tooltipReplyText.trim(), null, parentId);
    setTooltipReplyText('');
    setTooltipReplyTo(null);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Tag each row with its original index so selection/review status survives sort/filter
  const indexedRows = useMemo(() =>
    rows.map((row, i) => ({ ...row, _originalIndex: row._trueOriginalIndex !== undefined ? row._trueOriginalIndex : i })),
    [rows]
  );

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return indexedRows;
    return [...indexedRows].sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';
      const cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [indexedRows, sortConfig]);

  const filteredRows = useMemo(() => {
    if (!searchTerm) return sortedRows;
    const term = searchTerm.toLowerCase();
    return sortedRows.filter(row =>
      headers.some(h => (row[h] || '').toLowerCase().includes(term))
    );
  }, [sortedRows, searchTerm, headers]);

  // Get the current user's latest review decision for a row
  const getRowReviewStatus = (originalIndex) => {
    const decisions = rowReviews[String(originalIndex)];
    if (!decisions || decisions.length === 0) return null;
    const userDecision = [...decisions].reverse().find(d => d.alias === currentUserAlias);
    return userDecision ? userDecision.decision : null;
  };

  // Select All toggles all visible (filtered) rows
  const allVisibleSelected = filteredRows.length > 0 && filteredRows.every(r => selectedRows.has(r._originalIndex));

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    const next = new Set(selectedRows);
    if (allVisibleSelected) {
      filteredRows.forEach(r => next.delete(r._originalIndex));
    } else {
      filteredRows.forEach(r => next.add(r._originalIndex));
    }
    onSelectionChange(next);
  };

  const handleRowSelect = (originalIndex) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedRows);
    if (next.has(originalIndex)) {
      next.delete(originalIndex);
    } else {
      next.add(originalIndex);
    }
    onSelectionChange(next);
  };

  return (
    <div className="csv-table-container">
      <div className="table-toolbar">
        <input
          type="text"
          placeholder="Search table..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input search-input"
          aria-label="Search table"
        />
        <span className="row-count">{filteredRows.length} of {rows.length} rows</span>
        <div style={{ position: 'relative' }} ref={columnPickerRef}>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => setShowColumnPicker(!showColumnPicker)}
            title="Show/hide columns"
          >
            👁 Columns {hiddenColumns.size > 0 && `(${headers.length - hiddenColumns.size}/${headers.length})`}
          </button>
          {showColumnPicker && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, zIndex: 100,
              background: 'white', border: '1px solid var(--border, #ddd)', borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '8px 0', minWidth: 200, maxHeight: 300, overflowY: 'auto'
            }}>
              <div style={{ padding: '4px 12px 8px', fontSize: '0.75rem', color: '#888', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Toggle columns</span>
                <span style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => setHiddenColumns(new Set())}
                    style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.75rem' }}
                  >All</button>
                  <button
                    onClick={() => setHiddenColumns(new Set(headers))}
                    style={{ background: 'none', border: 'none', color: 'var(--danger, #d32f2f)', cursor: 'pointer', fontSize: '0.75rem' }}
                  >None</button>
                </span>
              </div>
              {headers.map(h => (
                <label key={h} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', cursor: 'pointer', fontSize: '0.82rem' }}>
                  <input
                    type="checkbox"
                    checked={!hiddenColumns.has(h)}
                    onChange={() => toggleColumn(h)}
                    style={{ cursor: 'pointer' }}
                  />
                  {h}
                </label>
              ))}
            </div>
          )}
        </div>
        {editable && <button className="btn btn-sm btn-secondary" onClick={onAddRow} style={{ marginLeft: 8 }}>+ Add Row</button>}
        {editable && <span className="edit-hint">Double-click to edit · Right-click to comment</span>}
      </div>

      <div className="table-scroll">
        <table className="csv-table" role="table">
          <thead>
            <tr>
              {selectable && (
                <th className="row-select-header">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={handleSelectAll}
                    aria-label="Select all visible rows"
                  />
                </th>
              )}
              <th className="row-num-header">#</th>
              {selectable && <th className="row-review-header">Status</th>}
              {editable && <th className="row-action-header"></th>}
              {codeGenEnabled && <th className="row-codegen-header">Code Gen</th>}
              {visibleHeaders.map(header => {
                const colClass = getColumnClass(header);
                return (
                <th
                  key={header}
                  onClick={() => handleSort(header)}
                  className={`sortable-header ${colClass}`}
                  role="columnheader"
                  aria-sort={sortConfig.key === header ? sortConfig.direction === 'asc' ? 'ascending' : 'descending' : 'none'}
                  style={columnWidths[header] ? { width: columnWidths[header], minWidth: columnWidths[header], maxWidth: columnWidths[header] } : undefined}
                >
                  {header}
                  {sortConfig.key === header && (
                    <span className="sort-indicator">{sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}</span>
                  )}
                  <span
                    className="col-resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, header)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
              ); })}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row, rowIndex) => {
              const origIdx = row._originalIndex;
              const reviewStatus = selectable ? getRowReviewStatus(origIdx) : null;
              return (
              <tr key={origIdx} className={`${rowIndex % 2 === 0 ? 'row-even' : 'row-odd'}${highlightedRow === origIdx ? ' row-highlighted' : ''}`} data-row-index={origIdx}>
                {selectable && (
                  <td className="row-select-cell">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(origIdx)}
                      onChange={() => handleRowSelect(origIdx)}
                      aria-label={`Select row ${origIdx + 1}`}
                    />
                  </td>
                )}
                <td className="row-num">{origIdx + 1}</td>
                {selectable && (
                  <td className="row-review-status">
                    {reviewStatus === 'approved' && <span title="Approved" className="review-icon approved">✅</span>}
                    {reviewStatus === 'rejected' && <span title="Rejected" className="review-icon rejected">❌</span>}
                  </td>
                )}
                {editable && (
                  <td className="row-action-cell">
                    <button
                      className="btn-delete-row"
                      onClick={() => onDeleteRow(origIdx)}
                      title={`Delete row ${origIdx + 1}`}
                      aria-label={`Delete row ${origIdx + 1}`}
                    >🗑</button>
                  </td>
                )}
                {codeGenEnabled && (
                  <td className="row-codegen-cell">
                    <button
                      className="btn-codegen-row"
                      onClick={() => onRowCodeGen && onRowCodeGen(origIdx)}
                      title={`Generate code for row ${origIdx + 1}`}
                      aria-label={`Generate code for row ${origIdx + 1}`}
                      disabled={generatingCode}
                    >🧬</button>
                    <button
                      className="btn-codegen-row btn-agent"
                      onClick={() => onRowCopyAgent && onRowCopyAgent(origIdx)}
                      title={`Copy agent prompt for row ${origIdx + 1}`}
                      aria-label={`Copy agent prompt for row ${origIdx + 1}`}
                      disabled={generatingCode}
                    >🤖</button>
                  </td>
                )}
                {visibleHeaders.map(header => {
                  const isEditing = editingCell?.rowIndex === origIdx && editingCell?.column === header;
                  const cellComments = getCellComments(origIdx, header);
                  const hasComments = cellComments.length > 0;
                  const isHovered = hoveredCell?.rowIndex === origIdx && hoveredCell?.column === header;
                  const cellThreads = getCellThreads(origIdx, header);
                  const threadStats = getCellThreadStats(origIdx, header);
                  const colClass = getColumnClass(header);
                  return (
                    <td
                      key={header}
                      className={`csv-cell ${colClass} ${editable ? 'editable' : ''} ${isEditing ? 'editing' : ''} ${hasComments ? 'has-comments' : ''}`}
                      onDoubleClick={() => handleDoubleClick(origIdx, header, row[header])}
                      onContextMenu={(e) => handleContextMenu(e, origIdx, header)}
                      onMouseEnter={() => {}}
                      onMouseLeave={() => {}}
                    >
                      {hasComments && (
                        <span
                          className={`comment-indicator ${threadStats.unresolved === 0 ? 'all-resolved' : ''}`}
                          title={`${threadStats.total} thread(s) — click to expand`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const cellRef = `R${origIdx + 1}:${header}`;
                            if (onCommentIconClick) onCommentIconClick(cellRef);
                            const isSame = pinnedCell?.rowIndex === origIdx && pinnedCell?.column === header;
                            if (isSame) {
                              setPinnedCell(null);
                              setHoveredCell(null);
                            } else {
                              setPinnedCell({ rowIndex: origIdx, column: header });
                              setHoveredCell({ rowIndex: origIdx, column: header });
                            }
                            setExpandedTooltipThread(null);
                            setTooltipReplyTo(null);
                            setTooltipReplyText('');
                          }}
                        >
                          {threadStats.unresolved === 0 ? '✅' : '💬'}
                          {threadStats.total > 1 && <span className="comment-count">{threadStats.total}</span>}
                        </span>
                      )}
                      {isEditing ? (
                        <textarea
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={handleEditSave}
                          onKeyDown={handleKeyDown}
                          className="cell-edit-input"
                          autoFocus
                          rows={3}
                          aria-label={`Edit ${header} for row ${origIdx + 1}`}
                        />
                      ) : (
                        <span className="cell-value">{row[header] || ''}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredRows.length === 0 && (
        <div className="empty-table">No matching rows found</div>
      )}

      {/* Click-away overlay for pinned comment tooltip */}
      {pinnedCell && (
        <div className="comment-overlay" onClick={() => { setPinnedCell(null); setHoveredCell(null); setExpandedTooltipThread(null); setTooltipReplyTo(null); setTooltipReplyText(''); }} />
      )}

      {/* Comment popup on right-click */}
      {commentPopup && (
        <>
          <div className="comment-overlay" onClick={() => setCommentPopup(null)} />
          <div className="comment-popup" style={{ top: commentPopup.y, left: commentPopup.x }}>
            <div className="popup-header">
              Comment on Row {commentPopup.rowIndex + 1}, {commentPopup.column}
            </div>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add your comment..."
              className="input popup-textarea"
              rows={3}
              autoFocus
              aria-label="Cell comment"
            />
            <div className="popup-actions">
              <button onClick={() => setCommentPopup(null)} className="btn btn-secondary btn-sm">Cancel</button>
              <button onClick={handleCommentSubmit} className="btn btn-primary btn-sm" disabled={!commentText.trim()}>
                Post
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default CsvTable;
